/**
 * The in-app viewer (28 Oct 2026): a receipt's photos, its factura's PDF and XML, and the portal agent's evidence open here, on
 * this page — nothing downloads unless the person presses Descargar. The bytes come from same-origin routes that always answer
 * as attachments with a sandboxing CSP (…/documents/{docId}/content, /api/operator/blob), so they are read, never navigated to
 * or framed: an <img> shows a photo; pdf.js draws a PDF on canvases (loaded on first use, no scripting, no WebAssembly — the
 * page's CSP allows neither); a CFDI is parsed with DOMParser — an inert document — into its summary and shown as text; JSON and
 * a HAR trace as text and lists. React escapes everything shown, so nothing in a file can run on this origin.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from './ui.tsx';

export type ViewKind = 'image' | 'pdf' | 'xml' | 'json' | 'har' | 'text' | 'auto';   // auto: a photo or a PDF, told apart by the bytes' type (a shared bill's voucher)
export interface ViewItem { key: string; label: string; kind: ViewKind; src: string; download?: string }

/** The kind of a file by its name (the evidence refs and other files that carry no kind of their own). */
export function kindOfName(name: string): ViewKind {
  const ext = name.slice(name.lastIndexOf('.') + 1).toLowerCase();
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'image';
  if (ext === 'pdf') return 'pdf';
  if (ext === 'xml') return 'xml';
  if (ext === 'har') return 'har';
  if (ext === 'json') return 'json';
  return 'text';
}

export function Viewer({ items, start = 0, onClose }: { items: ViewItem[]; start?: number; onClose: () => void }) {
  const [i, setI] = useState(Math.min(Math.max(start, 0), Math.max(items.length - 1, 0)));
  const item = items[i];
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') setI((n) => Math.min(n + 1, items.length - 1));
      else if (e.key === 'ArrowLeft') setI((n) => Math.max(n - 1, 0));
    };
    addEventListener('keydown', on);
    const overflow = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { removeEventListener('keydown', on); document.body.style.overflow = overflow; };
  }, [items.length, onClose]);
  if (!item) return null;
  return (
    <div className="viewer" role="dialog" aria-modal="true" aria-label={item.label}>
      <div className="viewer__bar">
        <div className="viewer__title">{item.label}</div>
        {item.download && <a className="viewer__btn" href={item.download} download aria-label="Descargar"><Icon.Download size={18} width={1.9} /><span>Descargar</span></a>}
        <button type="button" className="viewer__btn" onClick={onClose} aria-label="Cerrar"><Icon.Close size={20} /></button>
      </div>
      {items.length > 1 && (
        <div className="viewer__tabs" role="tablist">
          {items.map((it, n) => <button key={it.key} type="button" role="tab" aria-selected={n === i} className={`viewer__tab ${n === i ? 'viewer__tab--on' : ''}`} onClick={() => setI(n)}>{it.label}</button>)}
        </div>
      )}
      <div className="viewer__body" key={item.key}>
        {item.kind === 'auto' ? <AutoView src={item.src} label={item.label} />
          : item.kind === 'image' ? <ImageView src={item.src} label={item.label} />
          : item.kind === 'pdf' ? <PdfView src={item.src} />
          : item.kind === 'xml' ? <XmlView src={item.src} />
          : item.kind === 'har' ? <HarView src={item.src} />
          : <TextView src={item.src} json={item.kind === 'json'} />}
      </div>
    </div>
  );
}

function Failed({ error }: { error: string }) {
  return <div className="viewer__msg">No se pudo mostrar el archivo ({error}). Puedes descargarlo con el botón de arriba.</div>;
}
function Waiting() { return <div className="viewer__msg"><span className="spinner" /></div>; }

/** A file that is a photo or a PDF (a voucher): the content route types the bytes, so one look at the response decides. */
function AutoView({ src, label }: { src: string; label: string }) {
  const [kind, setKind] = useState<'image' | 'pdf' | 'error' | null>(null);
  useEffect(() => {
    let alive = true;
    fetch(src, { credentials: 'same-origin' }).then((r) => { if (!alive) return; const t = r.headers.get('content-type') ?? ''; setKind(!r.ok ? 'error' : /pdf/i.test(t) ? 'pdf' : 'image'); void r.body?.cancel(); })
      .catch(() => { if (alive) setKind('error'); });
    return () => { alive = false; };
  }, [src]);
  if (kind === null) return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><span className="spinner" /></div>;
  if (kind === 'error') return <div className="sub" style={{ padding: 24 }}>No se pudo abrir el archivo.</div>;
  return kind === 'pdf' ? <PdfView src={src} /> : <ImageView src={src} label={label} />;
}

function ImageView({ src, label }: { src: string; label: string }) {
  const [zoom, setZoom] = useState(false); const [broken, setBroken] = useState(false);
  if (broken) return <Failed error="la imagen no cargó" />;
  return (
    <div className={`viewer__img ${zoom ? 'viewer__img--zoom' : ''}`} onClick={() => setZoom((z) => !z)} title={zoom ? 'Toca para ajustar' : 'Toca para ampliar'}>
      <img src={src} alt={label} onError={() => setBroken(true)} />
    </div>
  );
}

/** Text of a same-origin route; the routes answer as attachments, which fetch() ignores. */
function useText(src: string): { text?: string; error?: string } {
  const [s, set] = useState<{ text?: string; error?: string }>({});
  useEffect(() => {
    let alive = true;
    fetch(src, { credentials: 'same-origin' })
      .then(async (r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); })
      .then((text) => { if (alive) set({ text }); }, (e: unknown) => { if (alive) set({ error: e instanceof Error ? e.message : String(e) }); });
    return () => { alive = false; };
  }, [src]);
  return s;
}

/** A PDF drawn page by page on canvases at the panel's width. pdf.js is imported on first use (it is about a megabyte). */
export function PdfView({ src }: { src: string }) {
  const host = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<{ pages?: number; error?: string }>({});
  const [zoom, setZoom] = useState(false);
  useEffect(() => {
    let cancelled = false; let destroy: (() => void) | undefined;
    (async () => {
      const [pdfjs, worker] = await Promise.all([import('pdfjs-dist'), import('pdfjs-dist/build/pdf.worker.min.mjs?url')]);
      pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
      const res = await fetch(src, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const task = pdfjs.getDocument({ data: new Uint8Array(await res.arrayBuffer()), useWasm: false, enableXfa: false });
      destroy = () => { void task.destroy(); };
      const doc = await task.promise;
      if (cancelled) return;
      setState({ pages: doc.numPages });
      const el = host.current; if (!el) return;
      // drawn at twice the panel's pixel density, so a page stays sharp when it is tapped to twice its width
      const width = Math.max(el.clientWidth - 2, 240); const dpr = Math.min(window.devicePixelRatio || 1, 2) * 2;
      for (let n = 1; n <= doc.numPages && !cancelled; n++) {
        const page = await doc.getPage(n);
        const vp = page.getViewport({ scale: (width / page.getViewport({ scale: 1 }).width) * dpr });
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(vp.width); canvas.height = Math.floor(vp.height);
        canvas.style.width = `${Math.floor(vp.width / dpr)}px`; canvas.className = 'viewer__page';
        canvas.setAttribute('aria-label', `Página ${n} de ${doc.numPages}`);
        el.appendChild(canvas);
        await page.render({ canvas, viewport: vp }).promise;
      }
    })().catch((e: unknown) => { if (!cancelled) setState({ error: e instanceof Error ? e.message : String(e) }); });
    const el = host.current;
    return () => { cancelled = true; destroy?.(); if (el) el.replaceChildren(); };
  }, [src]);
  return (
    <div className="viewer__pdf">
      {state.error ? <Failed error={state.error} /> : state.pages === undefined ? <Waiting /> : null}
      <div ref={host} className={`viewer__pages ${zoom ? 'viewer__pages--zoom' : ''}`} onClick={() => setZoom((z) => !z)} title={zoom ? 'Toca para ajustar' : 'Toca para ampliar'} />
    </div>
  );
}

// ── CFDI ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
const one = (doc: Document, local: string): Element | undefined => doc.getElementsByTagNameNS('*', local)[0] ?? undefined;
const all = (doc: Document, local: string): Element[] => [...doc.getElementsByTagNameNS('*', local)];
const money = (v: string | null | undefined, cur?: string | null) => {
  if (v == null || v === '') return null; const n = Number(v);
  return Number.isFinite(n) ? `${n.toLocaleString('es-MX', { style: 'currency', currency: cur && /^[A-Z]{3}$/.test(cur) && cur !== 'XXX' ? cur : 'MXN' })}` : v;
};

/** The CFDI's summary from its own attributes, or null when the XML is not a CFDI. */
export function cfdiSummary(doc: Document): { rows: [string, string][]; conceptos: { cantidad: string; descripcion: string; importe: string }[] } | null {
  const c = one(doc, 'Comprobante'); if (!c) return null;
  const e = one(doc, 'Emisor'); const r = one(doc, 'Receptor'); const t = one(doc, 'TimbreFiscalDigital');
  const cur = c.getAttribute('Moneda');
  const rows: [string, string | null | undefined][] = [
    ['Emisor', [e?.getAttribute('Nombre'), e?.getAttribute('Rfc')].filter(Boolean).join(' · ')],
    ['Receptor', [r?.getAttribute('Nombre'), r?.getAttribute('Rfc')].filter(Boolean).join(' · ')],
    ['CP y régimen del receptor', [r?.getAttribute('DomicilioFiscalReceptor'), r?.getAttribute('RegimenFiscalReceptor')].filter(Boolean).join(' · ')],
    ['Uso del CFDI', r?.getAttribute('UsoCFDI')],
    ['Folio fiscal (UUID)', t?.getAttribute('UUID')],
    ['Serie y folio', [c.getAttribute('Serie'), c.getAttribute('Folio')].filter(Boolean).join(' ')],
    ['Fecha', c.getAttribute('Fecha')?.replace('T', ' ')],
    ['Timbrado', t?.getAttribute('FechaTimbrado')?.replace('T', ' ')],
    ['Subtotal', money(c.getAttribute('SubTotal'), cur)],
    ['Descuento', money(c.getAttribute('Descuento'), cur)],
    ['Impuestos trasladados', money(all(doc, 'Impuestos').find((i) => i.hasAttribute('TotalImpuestosTrasladados'))?.getAttribute('TotalImpuestosTrasladados'), cur)],
    ['Total', money(c.getAttribute('Total'), cur)],
    ['Moneda', cur],
    ['Forma y método de pago', [c.getAttribute('FormaPago'), c.getAttribute('MetodoPago')].filter(Boolean).join(' · ')],
    ['Tipo', c.getAttribute('TipoDeComprobante')],
    ['Versión', c.getAttribute('Version')],
  ];
  const conceptos = all(doc, 'Concepto').slice(0, 60).map((k) => ({ cantidad: k.getAttribute('Cantidad') ?? '', descripcion: k.getAttribute('Descripcion') ?? '', importe: money(k.getAttribute('Importe'), cur) ?? '' }));
  return { rows: rows.filter((x): x is [string, string] => !!x[1]), conceptos };
}

/** The XML indented for reading (shown as text). */
export function prettyXml(doc: Document): string {
  const out: string[] = [];
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  const walk = (n: Node, depth: number) => {
    const pad = '  '.repeat(depth);
    if (n.nodeType === Node.ELEMENT_NODE) {
      const el = n as Element;
      const attrs = [...el.attributes].map((a) => `${a.name}="${esc(a.value)}"`);
      const open = attrs.length > 2 ? `${pad}<${el.tagName}\n${attrs.map((a) => `${pad}    ${a}`).join('\n')}` : `${pad}<${[el.tagName, ...attrs].join(' ')}`;
      const kids = [...el.childNodes].filter((k) => k.nodeType === Node.ELEMENT_NODE || (k.nodeType === Node.TEXT_NODE && k.textContent!.trim()));
      if (!kids.length) { out.push(`${open} />`); return; }
      if (kids.length === 1 && kids[0]!.nodeType === Node.TEXT_NODE) { out.push(`${open}>${esc(kids[0]!.textContent!.trim())}</${el.tagName}>`); return; }
      out.push(`${open}>`); for (const k of kids) walk(k, depth + 1); out.push(`${pad}</${el.tagName}>`);
    } else if (n.nodeType === Node.TEXT_NODE) out.push(`${pad}${esc(n.textContent!.trim())}`);
  };
  walk(doc.documentElement, 0);
  return out.join('\n');
}

function XmlView({ src }: { src: string }) {
  const { text, error } = useText(src);
  const [raw, setRaw] = useState(false);
  if (error) return <Failed error={error} />;
  if (text === undefined) return <Waiting />;
  const doc = new DOMParser().parseFromString(text.replace(/^﻿/, ''), 'application/xml');
  if (doc.getElementsByTagName('parsererror').length) return <div className="viewer__doc"><div className="viewer__msg">El XML no se pudo leer; así viene el archivo:</div><pre className="viewer__pre">{text}</pre></div>;
  const s = cfdiSummary(doc);
  return (
    <div className="viewer__doc">
      {s && (
        <>
          <div className="viewer__kvs">{s.rows.map(([k, v]) => <div key={k} className="kv"><span className="kv__k">{k}</span><span className="kv__v">{v}</span></div>)}</div>
          {s.conceptos.length > 0 && (
            <div className="viewer__conceptos">
              <div className="label label--xs">Conceptos</div>
              {s.conceptos.map((k, n) => <div key={n} className="viewer__concepto"><span className="mono">{k.cantidad}</span><span>{k.descripcion}</span><span className="mono">{k.importe}</span></div>)}
            </div>
          )}
        </>
      )}
      {s ? <button type="button" className="viewer__toggle" onClick={() => setRaw((x) => !x)}>{raw ? 'Ocultar el XML' : 'Ver el XML completo'}</button> : null}
      {(raw || !s) && <pre className="viewer__pre">{prettyXml(doc)}</pre>}
    </div>
  );
}

const MAX_TEXT = 2_000_000;
function TextView({ src, json }: { src: string; json: boolean }) {
  const { text, error } = useText(src);
  if (error) return <Failed error={error} />;
  if (text === undefined) return <Waiting />;
  let shown = text;
  if (json) { try { shown = JSON.stringify(JSON.parse(text), null, 2); } catch { /* not JSON after all: as it came */ } }
  const cut = shown.length > MAX_TEXT;
  return <div className="viewer__doc"><pre className="viewer__pre">{cut ? shown.slice(0, MAX_TEXT) : shown}</pre>{cut && <div className="viewer__msg">Se muestran los primeros 2 MB; descárgalo para verlo completo.</div>}</div>;
}

interface HarLike { log?: { entries?: { startedDateTime?: string; request?: { method?: string; url?: string }; response?: { status?: number } }[]; _viaticos?: { events?: { at?: string; kind?: string; data?: unknown }[] } } }
/** A run's trace: what the agent did (the job's own events), then every request the page made. */
function HarView({ src }: { src: string }) {
  const { text, error } = useText(src);
  if (error) return <Failed error={error} />;
  if (text === undefined) return <Waiting />;
  let har: HarLike; try { har = JSON.parse(text) as HarLike; } catch { return <TextBlock text={text} />; }
  const events = har.log?._viaticos?.events ?? []; const entries = har.log?.entries ?? [];
  const time = (s?: string) => (s ? s.slice(11, 19) : '');
  return (
    <div className="viewer__doc">
      {events.length > 0 && <Section title={`Lo que hizo el agente (${events.length})`}>{events.map((e, n) => <div key={n} className="viewer__ev"><span className="mono">{time(e.at)}</span><span className="viewer__evkind">{e.kind}</span><span className="mono viewer__evdata">{e.data === undefined ? '' : JSON.stringify(e.data).slice(0, 600)}</span></div>)}</Section>}
      <Section title={`Peticiones de la página (${entries.length})`}>{entries.map((e, n) => <div key={n} className="viewer__ev"><span className="mono">{time(e.startedDateTime)}</span><span className={`viewer__evkind ${(e.response?.status ?? 0) >= 400 ? 'tone-amber' : ''}`}>{e.request?.method} {e.response?.status}</span><span className="mono viewer__evdata">{e.request?.url}</span></div>)}</Section>
    </div>
  );
}
function Section({ title, children }: { title: string; children: ReactNode }) { return <div className="viewer__sec"><div className="label label--xs">{title}</div>{children}</div>; }
function TextBlock({ text }: { text: string }) { return <div className="viewer__doc"><pre className="viewer__pre">{text.slice(0, MAX_TEXT)}</pre></div>; }
